import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import { Card, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import { useDataProvider, Loading } from "react-admin";

interface HeatMapData {
  divisions: string[];
  departments: string[];
  data: [number, number, number][];
}

const RmfHeatMapChart: React.FC = () => {
  const theme = useTheme();
  const chartRef = useRef<HTMLDivElement>(null);
  const dataProvider = useDataProvider();
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [chartData, setChartData] = React.useState<HeatMapData | null>(null);

  useEffect(() => {
    let isMounted = true;
    setChartData(null);

    const fetchData = async () => {
      try {
        const response = await dataProvider.getChart(
          "rmf/analytics/department-division-heatmap-chart",
          {}
        );
        if (!isMounted) return;
        setChartData(response as HeatMapData);
      } catch (error) {
        if (isMounted) {
          setChartData(null);
          console.error("Error fetching RMF heatmap data:", error);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, [dataProvider]);

  useEffect(() => {
    if (!chartData) return;
    if (!chartRef.current) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const values = chartData.data.map((item) => item[2]);
    const maxValue = values.length ? Math.max(...values) : 0;

    const option: echarts.EChartsOption = {
      title: {
        text: "Active Risk Level by Division and Department",
        left: "center",
        textStyle: {
          color: theme.palette.mode === "dark" ? "#fff" : "#222",
          fontWeight: "bold",
          fontSize: "1.5em",
        },
      },
      tooltip: {
        position: "top",
        formatter: (params: any) => {
          const department = chartData.departments[params.data[0]];
          const division = chartData.divisions[params.data[1]];
          const score = params.data[2];
          return `${division} / ${department}<br/>Score: ${score}`;
        },
      },
      grid: {
        height: "85%",
        top: 60,
        left: 40,
        right: 80,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: chartData.departments,
        axisLabel: {
          color: theme.palette.mode === "dark" ? "#fff" : "#222",
          rotate: 45,
        },
        splitArea: { show: true },
      },
      yAxis: {
        type: "category",
        data: chartData.divisions,
        axisLabel: {
          color: theme.palette.mode === "dark" ? "#fff" : "#222",
        },
        splitArea: { show: true },
      },
      visualMap: {
        min: 0,
        max: maxValue || 1,
        calculable: true,
        orient: "vertical",
        left: "right",
        top: "middle",
        inRange: {
          color: ["#e0f3ff", "#1f78b4"],
        },
        textStyle: {
          color: theme.palette.mode === "dark" ? "#fff" : "#222",
        },
      },
      series: [
        {
          name: "Risk Score",
          type: "heatmap",
          data: chartData.data,
          emphasis: {
            itemStyle: {
              borderColor: "#333",
              borderWidth: 1,
            },
          },
          progressive: 0,
        },
      ],
    };

    chartInstance.current.setOption(option);
  }, [chartData, theme.palette.mode]);

  return (
    <Card
      sx={{
        height: 400,
        width: "100%",
        padding: "1em",
      }}
    >
      {!chartData ? (
        <Loading />
      ) : !Array.isArray(chartData.data) || chartData.data.length === 0 ? (
        <Box
          sx={{
            height: "100%",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="h6" sx={{ textAlign: "center" }}>
            No Data Found
          </Typography>
        </Box>
      ) : (
        <Box ref={chartRef} sx={{ height: "100%", width: "100%" }} />
      )}
    </Card>
  );
};

export default RmfHeatMapChart;
